/**
 * Catálogo único de permisos y roles. El backend los aplica y el frontend
 * los usa para ocultar acciones. Los códigos son estables (API pública).
 */
export const PERMISSIONS = {
  ALL: 'admin.all',
  USERS_MANAGE: 'users.manage',
  /** «Ver como»: abrir una sesión de solo lectura como otro usuario. Solo el administrador. */
  IMPERSONATE: 'users.impersonate',
  TERMINALS_MANAGE: 'terminals.manage',
  AUDIT_READ: 'audit.read',
  SETTINGS_MANAGE: 'settings.manage',
  PRODUCTS_READ: 'products.read',
  PRODUCTS_WRITE: 'products.write',
  SUPPLIERS_READ: 'suppliers.read',
  SUPPLIERS_WRITE: 'suppliers.write',
  STOCK_READ: 'stock.read',
  STOCK_MOVE: 'stock.move',
  PURCHASES_READ: 'purchases.read',
  PURCHASES_WRITE: 'purchases.write',
  PURCHASES_VALIDATE: 'purchases.validate',
  MIRRORS_READ: 'mirrors.read',
  MIRRORS_WRITE: 'mirrors.write',
  BOM_READ: 'bom.read',
  BOM_WRITE: 'bom.write',
  ORDERS_READ: 'orders.read',
  ORDERS_WRITE: 'orders.write',
  KITS_READ: 'kits.read',
  KITS_SCAN: 'kits.scan',
  KITS_COMPLETE: 'kits.complete',
  PACKAGING_READ: 'packaging.read',
  PACKAGING_WRITE: 'packaging.write',
  LOGISTICS_READ: 'logistics.read',
  LOGISTICS_WRITE: 'logistics.write',
  PLANNING_READ: 'planning.read',
  FINANCE_READ: 'finance.read',
  FINANCE_WRITE: 'finance.write',
  PLANNING_RUN: 'planning.run',
  DASHBOARD_READ: 'dashboard.read'
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const PERMISSION_CATALOG: { code: PermissionCode; module: string; description: string }[] = [
  { code: PERMISSIONS.USERS_MANAGE, module: 'Seguridad', description: 'Gestionar usuarios y roles' },
  { code: PERMISSIONS.IMPERSONATE, module: 'Seguridad', description: 'Ver la aplicación como otro usuario, solo lectura' },
  { code: PERMISSIONS.TERMINALS_MANAGE, module: 'Seguridad', description: 'Gestionar terminales de fábrica' },
  { code: PERMISSIONS.AUDIT_READ, module: 'Seguridad', description: 'Consultar la auditoría' },
  { code: PERMISSIONS.SETTINGS_MANAGE, module: 'Seguridad', description: 'Modificar la configuración' },
  { code: PERMISSIONS.PRODUCTS_READ, module: 'Artículos', description: 'Consultar artículos' },
  { code: PERMISSIONS.PRODUCTS_WRITE, module: 'Artículos', description: 'Crear y editar artículos' },
  { code: PERMISSIONS.SUPPLIERS_READ, module: 'Compras', description: 'Consultar proveedores' },
  { code: PERMISSIONS.SUPPLIERS_WRITE, module: 'Compras', description: 'Crear y editar proveedores' },
  { code: PERMISSIONS.STOCK_READ, module: 'Almacén', description: 'Consultar stock y movimientos' },
  { code: PERMISSIONS.STOCK_MOVE, module: 'Almacén', description: 'Registrar movimientos y regularizaciones' },
  { code: PERMISSIONS.PURCHASES_READ, module: 'Compras', description: 'Consultar facturas y pedidos de compra' },
  { code: PERMISSIONS.PURCHASES_WRITE, module: 'Compras', description: 'Crear facturas y pedidos de compra' },
  { code: PERMISSIONS.PURCHASES_VALIDATE, module: 'Compras', description: 'Validar facturas y recepciones' },
  { code: PERMISSIONS.MIRRORS_READ, module: 'Espejos', description: 'Consultar modelos y unidades' },
  { code: PERMISSIONS.MIRRORS_WRITE, module: 'Espejos', description: 'Crear y editar modelos' },
  { code: PERMISSIONS.BOM_READ, module: 'Espejos', description: 'Consultar BOM' },
  { code: PERMISSIONS.BOM_WRITE, module: 'Espejos', description: 'Editar y activar BOM' },
  { code: PERMISSIONS.ORDERS_READ, module: 'Pedidos', description: 'Consultar pedidos de cliente' },
  { code: PERMISSIONS.ORDERS_WRITE, module: 'Pedidos', description: 'Crear y confirmar pedidos' },
  { code: PERMISSIONS.KITS_READ, module: 'Kits', description: 'Consultar kits' },
  { code: PERMISSIONS.KITS_SCAN, module: 'Kits', description: 'Escanear componentes de kits' },
  { code: PERMISSIONS.KITS_COMPLETE, module: 'Kits', description: 'Cerrar kits y consumir stock' },
  { code: PERMISSIONS.PACKAGING_READ, module: 'Embalaje', description: 'Consultar embalajes' },
  { code: PERMISSIONS.PACKAGING_WRITE, module: 'Embalaje', description: 'Fabricar y consumir embalajes' },
  { code: PERMISSIONS.LOGISTICS_READ, module: 'Logística', description: 'Consultar expediciones' },
  { code: PERMISSIONS.LOGISTICS_WRITE, module: 'Logística', description: 'Crear expediciones y etiquetas' },
  { code: PERMISSIONS.PLANNING_READ, module: 'Planificación', description: 'Consultar MRP y alertas' },
  { code: PERMISSIONS.PLANNING_RUN, module: 'Planificación', description: 'Ejecutar MRP' },
  { code: PERMISSIONS.FINANCE_READ, module: 'Tesorería', description: 'Consultar cuentas, formas de pago y movimientos' },
  { code: PERMISSIONS.FINANCE_WRITE, module: 'Tesorería', description: 'Gestionar cuentas y registrar cobros y pagos' },
  { code: PERMISSIONS.DASHBOARD_READ, module: 'Dirección', description: 'Ver el cuadro de mando' }
];

export interface RoleDefinition {
  code: string;
  name: string;
  description: string;
  permissions: PermissionCode[];
}

export const ROLES: RoleDefinition[] = [
  {
    code: 'admin',
    name: 'Administrador',
    description: 'Acceso completo, usuarios y configuración',
    permissions: [PERMISSIONS.ALL]
  },
  {
    code: 'purchasing',
    name: 'Compras',
    description: 'Proveedores, facturas, artículos, costes y pedidos de compra',
    permissions: [
      PERMISSIONS.PRODUCTS_READ, PERMISSIONS.PRODUCTS_WRITE,
      PERMISSIONS.SUPPLIERS_READ, PERMISSIONS.SUPPLIERS_WRITE,
      PERMISSIONS.STOCK_READ, PERMISSIONS.PURCHASES_READ, PERMISSIONS.PURCHASES_WRITE,
      PERMISSIONS.PURCHASES_VALIDATE, PERMISSIONS.MIRRORS_READ, PERMISSIONS.BOM_READ,
      PERMISSIONS.PLANNING_READ, PERMISSIONS.FINANCE_READ, PERMISSIONS.FINANCE_WRITE,
      PERMISSIONS.DASHBOARD_READ
    ]
  },
  {
    code: 'warehouse',
    name: 'Almacén',
    description: 'Recepciones, stock, inventario, etiquetas y preparación de kits',
    permissions: [
      PERMISSIONS.PRODUCTS_READ, PERMISSIONS.STOCK_READ, PERMISSIONS.STOCK_MOVE,
      PERMISSIONS.KITS_READ, PERMISSIONS.KITS_SCAN, PERMISSIONS.KITS_COMPLETE,
      PERMISSIONS.MIRRORS_READ, PERMISSIONS.BOM_READ, PERMISSIONS.ORDERS_READ,
      PERMISSIONS.PACKAGING_READ, PERMISSIONS.PURCHASES_READ, PERMISSIONS.PURCHASES_VALIDATE,
      PERMISSIONS.PLANNING_READ, PERMISSIONS.DASHBOARD_READ
    ]
  },
  {
    code: 'packing',
    name: 'Embalaje',
    description: 'Órdenes de embalaje, Panotec y consumo de cartón',
    permissions: [
      PERMISSIONS.PRODUCTS_READ, PERMISSIONS.STOCK_READ, PERMISSIONS.KITS_READ,
      PERMISSIONS.PACKAGING_READ, PERMISSIONS.PACKAGING_WRITE, PERMISSIONS.MIRRORS_READ,
      PERMISSIONS.ORDERS_READ, PERMISSIONS.PLANNING_READ, PERMISSIONS.DASHBOARD_READ
    ]
  },
  {
    code: 'logistics',
    name: 'Logística',
    description: 'Expediciones, transportistas, etiquetas y tracking',
    permissions: [
      PERMISSIONS.PRODUCTS_READ, PERMISSIONS.STOCK_READ, PERMISSIONS.ORDERS_READ,
      PERMISSIONS.LOGISTICS_READ, PERMISSIONS.LOGISTICS_WRITE, PERMISSIONS.PACKAGING_READ,
      PERMISSIONS.KITS_READ, PERMISSIONS.PLANNING_READ, PERMISSIONS.DASHBOARD_READ
    ]
  },
  {
    code: 'management',
    name: 'Dirección',
    description: 'KPIs, riesgos, consultas y planificación',
    permissions: [
      PERMISSIONS.DASHBOARD_READ, PERMISSIONS.FINANCE_READ, PERMISSIONS.PLANNING_READ, PERMISSIONS.PLANNING_RUN,
      PERMISSIONS.PRODUCTS_READ, PERMISSIONS.STOCK_READ, PERMISSIONS.PURCHASES_READ,
      PERMISSIONS.ORDERS_READ, PERMISSIONS.KITS_READ, PERMISSIONS.MIRRORS_READ,
      PERMISSIONS.BOM_READ, PERMISSIONS.PACKAGING_READ, PERMISSIONS.LOGISTICS_READ,
      PERMISSIONS.AUDIT_READ
    ]
  }
];

export function rolePermissions(roleCodes: string[]): Set<string> {
  const result = new Set<string>();
  for (const role of ROLES) {
    if (roleCodes.includes(role.code)) role.permissions.forEach((p) => result.add(p));
  }
  return result;
}

export function hasPermission(granted: Iterable<string>, required: PermissionCode): boolean {
  const set = granted instanceof Set ? granted : new Set(granted);
  return set.has(PERMISSIONS.ALL) || set.has(required);
}
